import axios, { AxiosResponse } from "axios";
import { ArrayFromEnvelope, configService, ObjectFromEnvelope } from "./config.service";
import { IPoolPost, IPoolPut, Pool } from "../models/pools";
import { convertDateOnlyToUtc } from "../utils/dateUtils";
import { WorkHourPoolStatus } from "../models/workHourPoolStatus";
import { UserApiData } from "@/modules/security/models/user-model";
import store from "@/store";
import { useUser } from "@/composables/apiModels/useUser";
import { getQueryClient } from "../queryClient";

class PoolService {
  async fetchPools(): Promise<Pool[]> {
    const user: UserApiData = store.getters["security/getUser"]();
    const { isManager } = useUser();
    let url = "";
    if (isManager(user)) {
      url = configService.APIInstitutionUrl(
        "work_hour_pools.json?institution_id=" + configService.institutionId()
      );
    } else {
      const employee = store.getters["employees/GetEmployeeById"](user.employee_id);
      if (!employee || !employee.allEmployments) {
        return [];
      }
      const employmentIds = employee.allEmployments().map((e) => e.id);
      if (employmentIds.length === 0) {
        return [];
      }
      url = configService.personaleOrAdminUrl(
        "work_hour_pools.json?employment_ids=" + employmentIds.toString()
      );
    }
    return new Promise((resolve, reject) => {
      axios
        .get<Pool[]>(url)
        .then((response: AxiosResponse) => {
          resolve(Pool.fromJSONArray(ArrayFromEnvelope<Pool>(response.data)));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
  async fetchPoolsByEmploymentId(employmentId: number): Promise<Pool[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<Pool[]>(
          configService.personaleOrAdminUrl("work_hour_pools.json?employment_id=" + employmentId)
        )
        .then((response: AxiosResponse) => {
          resolve(Pool.fromJSONArray(ArrayFromEnvelope<Pool>(response.data)));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
  async createPool(pool: Pool): Promise<Pool> {
    return new Promise((resolve, reject) => {
      axios
        .post<Pool>(
          configService.APIInstitutionUrl("work_hour_pools.json"),
          pool.convertToPostObject()
        )
        .then((response: AxiosResponse) => {
          resolve(Pool.fromJSON(ObjectFromEnvelope<Pool>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }
  async updatePool(pool: Pool): Promise<Pool> {
    return new Promise((resolve, reject) => {
      axios
        .put<Pool>(
          configService.APIInstitutionUrl("work_hour_pools/" + pool.id + ".json"),
          pool.convertToPutObject()
        )
        .then((response: AxiosResponse) => {
          resolve(Pool.fromJSON(ObjectFromEnvelope<Pool>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }
  async createUpdatePools(obj: {
    pools_to_create: IPoolPost[];
    pools_to_update: IPoolPut[];
  }): Promise<{ createdPools: Pool[]; updatedPools: Pool[]; errors: string[] }> {
    return new Promise((resolve, reject) => {
      axios
        .post<Pool>(
          configService.APIInstitutionUrl("work_hour_pools/create_update_pools.json"),
          obj
        )
        .then(async (response: AxiosResponse) => {
          const createdPools: Pool[] = await Pool.fromJSONArray<Pool>(
            response.data.data.created_work_hour_pools
          );
          const updatedPools: Pool[] = await Pool.fromJSONArray<Pool>(
            response.data.data.updated_work_hour_pools
          );
          const errors = response.data.data.errors;
          resolve({ createdPools, updatedPools, errors });
        })
        .catch((error) => reject(error));
    });
  }
  async deletePool(poolId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .delete<Pool>(configService.APIInstitutionUrl("work_hour_pools/" + poolId + ".json"))
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  }
  async getPoolStatus(date: Date): Promise<WorkHourPoolStatus> {
    const utcDate = convertDateOnlyToUtc(date);
    const institutionId = configService.institutionId();
    // Go through react-query so concurrent callers (e.g. a plan view's getData
    // and EmploymentTimeOverview) share a single in-flight request instead of
    // each hitting the endpoint. staleTime 0 keeps it always-fresh — the Vuex
    // pool cache handles "already loaded", and a pools/reset must refetch.
    const raw = await getQueryClient().fetchQuery({
      queryKey: ["workHourPoolStatus", institutionId, utcDate],
      queryFn: async () => {
        const response = await axios.get(
          configService.personaleOrAdminUrl(
            "work_hour_pool_status.json?date=" + utcDate
          )
        );
        return ObjectFromEnvelope<WorkHourPoolStatus>(response.data);
      },
      staleTime: 0
    });
    return WorkHourPoolStatus.fromJSON(raw);
  }

}

export const poolService: PoolService = new PoolService();
